import type { ChatMessage, ConversationPage, ConversationSummary } from '../types';

import { getJson, postJson, put } from './http';

// Sessions are created lazily (see useChat) and expire server-side. There is
// deliberately no delete call: a browser can be closed, crash, or lose the network
// at any point, so client-side cleanup can never be the mechanism that bounds
// session lifetime -- an idle TTL on the AI service is. Add an explicit close only
// if the service asks for one, and then as a `keepalive` DELETE from a `pagehide`
// handler guarded on `event.persisted === false`, so a page entering the
// back/forward cache isn't torn down behind a user who may return to it.
export async function createSession(): Promise<string> {
  const data = await postJson('/sessions', {}, 'create session');
  // Fail loudly on an unexpected body shape (an API-version drift to `session_id`,
  // an empty object) rather than returning undefined: that would be cached as a
  // "created" session and every later turn would send a session-less request the
  // server silently rejects.
  if (typeof data?.sessionId !== 'string' || !data.sessionId) {
    throw new Error('Session response did not include a valid sessionId');
  }
  return data.sessionId;
}

export interface ResumedSession {
  sessionId: string;
  messages: ChatMessage[];
}

// The conversation this user should rejoin -- their most recent one within the
// service's resume window -- or null if there isn't one. Lets a reload or a second
// tab continue the existing thread instead of opening an empty one.
//
// `uiMessages` is the stored transcript already rendered as bubbles by the service
// (app/api/transcript.py); the raw Anthropic message list it also returns is not
// something this UI can display.
export async function fetchCurrentSession(): Promise<ResumedSession | null> {
  const data = await getJson('/sessions/current', 'load current session');
  if (!data || typeof data.sessionId !== 'string' || !data.sessionId) {
    return null;
  }
  return {
    sessionId: data.sessionId,
    messages: Array.isArray(data.uiMessages) ? (data.uiMessages as ChatMessage[]) : [],
  };
}

// Flushes a finished conversation to the service's durable tier. The thread is
// still readable either way, so most callers can treat this as best-effort -- but
// it still rejects on failure, since useChat's "New Chat" needs to know when an
// archive it depended on didn't land.
export async function archiveSession(sessionId: string): Promise<void> {
  await put(`/sessions/${encodeURIComponent(sessionId)}`, 'archive session');
}

// One page of the caller's conversations, most recently active first. Rows carry a
// preview and a message count, not transcripts -- opening one is a separate
// `fetchSession` call.
//
// Paged rather than date-windowed: the service seeks by cursor through an index, so
// a page costs the same however much history there is, and scrolling reaches
// everything rather than stopping at an arbitrary lookback.
export async function listSessions(
  limit: number,
  cursor?: string | null
): Promise<ConversationPage> {
  const query = `?limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
  const data = await getJson(`/sessions${query}`, 'load conversations');
  if (!data || !Array.isArray(data.sessions)) {
    return { conversations: [], nextCursor: null };
  }
  return {
    conversations: data.sessions as ConversationSummary[],
    nextCursor: typeof data.nextCursor === 'string' ? data.nextCursor : null,
  };
}

// One conversation's transcript, live or archived. A pure read on the service side:
// opening a past conversation does not make it the one a later reload resumes into.
// Sending a message into it does.
export async function fetchSession(sessionId: string): Promise<ChatMessage[]> {
  const data = await getJson(`/sessions/${encodeURIComponent(sessionId)}`, 'load conversation');
  return Array.isArray(data?.uiMessages) ? (data.uiMessages as ChatMessage[]) : [];
}
